import { useState, useRef, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { RotateCcw, ChevronRight, Smartphone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

// Types
interface BotMessage {
  id: string;
  from: "bot" | "user";
  text: string;
  buttons?: BotButton[];
  isAudio?: boolean;
  isProgress?: boolean;
  isPhoto?: boolean;
}

interface BotButton {
  text: string;
  action: string;
}

// Fetch real data from DB
function useEvents() {
  return useQuery({
    queryKey: ["sim-events"],
    queryFn: async () => {
      const { data } = await supabase
        .from("events")
        .select("id, name, emoji")
        .eq("is_active", true)
        .order("sort_order");
      return data || [];
    },
  });
}

function useGenres() {
  return useQuery({
    queryKey: ["sim-genres"],
    queryFn: async () => {
      const { data } = await supabase
        .from("genres")
        .select("id, name, emoji")
        .eq("is_active", true)
        .order("sort_order");
      return data || [];
    },
  });
}

function useVocals() {
  return useQuery({
    queryKey: ["sim-vocals"],
    queryFn: async () => {
      const { data } = await supabase
        .from("vocals")
        .select("id, name, emoji")
        .eq("is_active", true)
        .order("sort_order");
      return data || [];
    },
  });
}

function useHitPackages() {
  return useQuery({
    queryKey: ["sim-packages"],
    queryFn: async () => {
      const { data } = await supabase
        .from("settings")
        .select("value")
        .eq("key", "hit_packages")
        .single();
      if (data?.value && typeof data.value === "object") {
        return data.value as Record<string, { price: number; name: string; emoji: string; savings: string | null }>;
      }
      return {
        "1": { price: 249, name: "1 Хит", emoji: "🔥", savings: null },
        "5": { price: 949, name: "5 Хитов", emoji: "🎶", savings: "Популярно" },
        "10": { price: 1649, name: "10 Хитов", emoji: "💎", savings: null },
        "15": { price: 2249, name: "15 Хитов", emoji: "👥", savings: "Лучший выбор для компании" },
      };
    },
  });
}

function useFullTrackPrice() {
  return useQuery({
    queryKey: ["sim-full-price"],
    queryFn: async () => {
      const { data } = await supabase
        .from("settings")
        .select("value")
        .eq("key", "full_track_price")
        .maybeSingle();
      return data?.value ? Number(data.value) : 249;
    },
  });
}

// Message bubble
function MessageBubble({ msg, onButtonClick }: { msg: BotMessage; onButtonClick: (action: string) => void }) {
  const isBot = msg.from === "bot";

  return (
    <div className={cn("flex w-full mb-2", isBot ? "justify-start" : "justify-end")}>
      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed shadow-sm",
          isBot
            ? "bg-card border border-border text-foreground rounded-bl-md"
            : "bg-primary text-primary-foreground rounded-br-md"
        )}
      >
        {msg.isPhoto && (
          <div className="mb-2 rounded-lg overflow-hidden bg-muted/50 aspect-video flex items-center justify-center">
            <span className="text-3xl">🎁</span>
          </div>
        )}
        {msg.isAudio && (
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs">🎵</div>
            <div className="flex-1">
              <div className="h-1.5 bg-primary/20 rounded-full w-full">
                <div className="h-1.5 bg-primary/60 rounded-full w-3/4" />
              </div>
              <span className="text-[10px] text-muted-foreground mt-0.5">0:32</span>
            </div>
          </div>
        )}
        {msg.isProgress ? (
          <div>
            <div dangerouslySetInnerHTML={{ __html: msg.text.replace(/\n/g, "<br/>") }} />
            <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full animate-pulse" style={{ width: "65%" }} />
            </div>
          </div>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: msg.text.replace(/\n/g, "<br/>") }} />
        )}
        {msg.buttons && msg.buttons.length > 0 && (
          <div className="flex flex-col gap-1 mt-2">
            {msg.buttons.map((btn, i) => (
              <button
                key={i}
                onClick={() => onButtonClick(btn.action)}
                className="w-full text-center py-1.5 px-3 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-[12px] font-medium transition-colors border border-primary/20"
              >
                {btn.text}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Main simulator
export default function BotSimulator() {
  const [messages, setMessages] = useState<BotMessage[]>([]);
  const [simState, setSimState] = useState<Record<string, any>>({ step: "start" });
  const chatRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(0);

  const { data: events } = useEvents();
  const { data: genres } = useGenres();
  const { data: vocals } = useVocals();
  const { data: packages } = useHitPackages();
  const { data: fullTrackPrice } = useFullTrackPrice();

  const PRICE = fullTrackPrice || 249;

  const nextId = () => String(++idRef.current);

  const addMsg = useCallback((from: "bot" | "user", text: string, buttons?: BotButton[], opts?: { isAudio?: boolean; isProgress?: boolean; isPhoto?: boolean }) => {
    setMessages((prev) => [
      ...prev,
      { id: nextId(), from, text, buttons, isAudio: opts?.isAudio, isProgress: opts?.isProgress, isPhoto: opts?.isPhoto },
    ]);
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (chatRef.current) {
      setTimeout(() => {
        chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
      }, 50);
    }
  }, [messages]);

  // Start on mount
  const startedRef = useRef(false);
  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true;
      runStep("start");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    setMessages([]);
    setSimState({ step: "start" });
    idRef.current = 0;
    setTimeout(() => runStep("start"), 100);
  };

  const runStep = (step: string, payload?: any) => {
    switch (step) {
      // ====== ONBOARDING ======
      case "start":
        addMsg("bot", `👨‍🍳 <b>Привет! Это Хит Шеф.</b>\n\nЗдесь создают персональные песни\nпро себя или друзей всего за 3 минуты 🎶\n\nНемного магии — и у вас свой хит.`, [
          { text: "🔥 Попробовать бесплатно", action: "onboarding_try" },
        ]);
        setSimState({ step: "onboarding_1" });
        break;

      case "onboarding_try":
        addMsg("user", "🔥 Попробовать бесплатно");
        setTimeout(() => {
          addMsg("bot", `🎁 <b>У вас есть 1 бесплатный пробный хит.</b>\n\nВ него входит:\n• текст песни\n• 2 демо-версии\n\nЕсли понравится — можно получить полную версию и создавать новые хиты.`, [
            { text: "🎵 Создать пробный хит", action: "select_event" },
          ]);
          setSimState({ step: "onboarding_2" });
        }, 400);
        break;

      // ====== EVENT → GENRE → VOCAL → STORY ======
      case "select_event":
        addMsg("user", "🎵 Создать пробный хит");
        setTimeout(() => {
          const btns = (events || []).map((e) => ({
            text: `${e.emoji} ${e.name}`,
            action: `event:${e.id}:${e.emoji} ${e.name}`,
          }));
          addMsg("bot", `🎤 <b>Отлично! Выбери событие для песни:</b>`, btns);
          setSimState({ step: "select_event" });
        }, 400);
        break;

      case "event_selected": {
        const [, eventId, ...nameParts] = (payload as string).split(":");
        const eventName = nameParts.join(":");
        addMsg("user", eventName);
        setTimeout(() => {
          const btns = (genres || []).map((g) => ({
            text: `${g.emoji} ${g.name}`,
            action: `genre:${g.id}:${g.emoji} ${g.name}`,
          }));
          addMsg("bot", `✅ Событие: <b>${eventName}</b>\n\n🎵 Теперь выбери жанр музыки:`, btns);
          setSimState((s) => ({ ...s, step: "select_genre", eventName }));
        }, 400);
        break;
      }

      case "genre_selected": {
        const [, genreId, ...nameParts] = (payload as string).split(":");
        const genreName = nameParts.join(":");
        addMsg("user", genreName);
        setTimeout(() => {
          const btns = (vocals || []).map((v) => ({
            text: `${v.emoji} ${v.name}`,
            action: `vocal:${v.id}:${v.emoji} ${v.name}`,
          }));
          addMsg(
            "bot",
            `✅ Событие: <b>${simState.eventName}</b>\n✅ Жанр: <b>${genreName}</b>\n\n🎤 <b>Выбери тип вокала:</b>`,
            btns
          );
          setSimState((s) => ({ ...s, step: "select_vocal", genreName }));
        }, 400);
        break;
      }

      case "vocal_selected": {
        const [, vocalId, ...nameParts] = (payload as string).split(":");
        const vocalName = nameParts.join(":");
        addMsg("user", vocalName);
        setTimeout(() => {
          addMsg(
            "bot",
            `✅ Событие: <b>${simState.eventName}</b>\n✅ Жанр: <b>${simState.genreName}</b>\n✅ Вокал: <b>${vocalName}</b>\n\nОсталось самое главное\n\n💬 <b>Напиши то, что сделает песню по-настоящему личной</b> (достаточно 3-5 основных фактов)\n\nНапример:\n• Имена\n• Особенности внешности, поведения и характера\n• Смешные истории или любимые фразы\n• Профессия\n• Мечты\n• Наличие семьи, их имена и особенности\n• Место знакомства\n• И любые другие факты\n\n🎤 Хочешь — расскажи голосом, я всё распознаю.`,
            [{ text: "(Ввести историю)", action: "enter_story" }]
          );
          setSimState((s) => ({ ...s, step: "enter_story", vocalName }));
        }, 400);
        break;
      }

      // ====== STORY CONFIRM ======
      case "enter_story":
        addMsg("user", "Это мой друг Антон. Он обожает рыбалку и мороженое. Работает программистом, мечтает объехать весь мир. Любит шутить про котов 😄");
        setTimeout(() => {
          addMsg(
            "bot",
            `📋 <b>Проверь данные:</b>\n\n🎉 Событие: ${simState.eventName}\n🎵 Жанр: ${simState.genreName}\n\n📝 История:\n<i>Это мой друг Антон. Он обожает рыбалку и мороженое. Работает программистом, мечтает объехать весь мир. Любит шутить про котов 😄</i>\n\nВсё верно? Жми кнопку для генерации!`,
            [
              { text: "✅ Создать хит!", action: "confirm_generate" },
              { text: "✏️ Изменить историю", action: "edit_story" },
            ]
          );
          setSimState((s) => ({ ...s, step: "confirm" }));
        }, 400);
        break;

      // ====== EDIT STORY ======
      case "edit_story":
        addMsg("user", "✏️ Изменить историю");
        setTimeout(() => {
          addMsg("bot", `Это мой друг Антон. Он обожает рыбалку и мороженое. Работает программистом, мечтает объехать весь мир. Любит шутить про котов 😄`);
          setTimeout(() => {
            addMsg("bot", `✏️ <b>Скопируйте текст выше, исправьте и отправьте обратно.</b>\n\nИли запишите голосовое 🎤 с новой версией.`, [
              { text: "(Отправить исправленную версию)", action: "enter_story" },
            ]);
          }, 200);
        }, 400);
        break;

      // ====== LYRICS GENERATION ======
      case "confirm_generate":
        addMsg("user", "✅ Создать хит!");
        setTimeout(() => {
          addMsg(
            "bot",
            `👨‍🍳 <b>Готовлю текст песни</b>\n\n🥣 Подбираю ингредиенты... 0:00\n\n<code>▱▱▱▱▱▱▱▱▱▱</code>`,
            undefined,
            { isProgress: true }
          );
        }, 300);
        setTimeout(() => {
          const sampleLyrics = `# АНТОХА — КОРОЛЬ МОРОЖЕНОГО\n\n[Куплет 1]\nАнтоха — рыбак и мечтатель,\nС удочкой в руке — настоящий искатель,\nМороженое тает, а он — программист,\nВ коде как в океане — настоящий артист!\n\n[Припев]\nАнтоха! Антоха! Ты — наш герой!\nС котами шутишь — и смех рекой!\nМир объедешь — это не вопрос,\nАнтоха — ты лучший, без всяких грёз!`;
          addMsg(
            "bot",
            `🔥 <b>Текст готов!</b>\n\n${sampleLyrics}\n\nСейчас создаю демо-версии, чтобы вы услышали, как это звучит.\n\n👇 <b>Всё ок? Жми "Делаем песню!"</b>`,
            [
              { text: "🚀 Делаем песню!", action: "generate_music" },
              { text: "✏️ Нужны правки", action: "need_edits" },
            ]
          );
          setSimState((s) => ({ ...s, step: "confirm_lyrics" }));
        }, 2000);
        break;

      // ====== EDIT LYRICS ======
      case "need_edits":
        addMsg("user", "✏️ Нужны правки");
        setTimeout(() => {
          addMsg(
            "bot",
            `✏️ <b>Что изменить?</b>\n\nНапиши или запиши голосовое 🎤 одной фразой, например:\n• <i>"добавь про рыбалку"</i>\n• <i>"сделай припев веселее"</i>\n• <i>"убери второй куплет"</i>\n\n💡 Одна правка — и генерируем музыку!`,
            [{ text: '(Написать "сделай веселее")', action: "send_edit" }]
          );
        }, 400);
        break;

      case "send_edit":
        addMsg("user", "сделай припев веселее и добавь больше про котов");
        setTimeout(() => {
          addMsg("bot", `✨ Вношу правки в текст...`);
        }, 300);
        setTimeout(() => {
          addMsg(
            "bot",
            `✅ <b>Текст обновлён!</b>\n\n# АНТОХА — КОРОЛЬ МОРОЖЕНОГО\n\n[Куплет 1]\nАнтоха — рыбак и мечтатель...\n<i>(обновлённая версия с котами)</i>\n\n👇 <b>Готово! Жми "Делаем песню!"</b>`,
            [{ text: "🚀 Делаем песню!", action: "generate_music" }]
          );
        }, 1500);
        break;

      // ====== MUSIC GENERATION + DEMO DELIVERY (FREE FLOW) ======
      case "generate_music":
        addMsg("user", "🚀 Делаем песню!");
        setTimeout(() => {
          addMsg(
            "bot",
            `🍳 <b>Готовлю музыкальный хит</b>\n\n🔥 Разогреваю духовку...\n\n⏱ Обычно занимает 2-3 минуты\nРезультат придёт сообщением ✨`,
            undefined,
            { isProgress: true }
          );
        }, 300);
        setTimeout(() => {
          // Demo 1
          addMsg("bot", `🎵 <b>Версия 1 из 2</b> (~40 сек)\n\n🎧 Это пробная версия.\nВ полной версии — финальный мастеринг и чистый звук.`, undefined, { isAudio: true });
        }, 2000);
        setTimeout(() => {
          // Demo 2
          addMsg("bot", `🎵 <b>Версия 2 из 2</b> (~40 сек)\n\n🎧 Это пробная версия.\nВ полной версии — финальный мастеринг и чистый звук.`, undefined, { isAudio: true });
        }, 2300);
        setTimeout(() => {
          // Upsell message
          addMsg(
            "bot",
            `👨‍🍳 <b>ВАУ!</b>\nПолучился реально крутой хит 😳🔥\n\nЭто пока только демо-версия.\nПолная версия звучит ещё лучше!\n\n👇 Выберите вариант и доведём до идеала.`,
            [
              { text: `💳 Оплатить ${PRICE}₽`, action: "buy_full_direct" },
              { text: "🎟 Ввести промокод", action: "enter_promo" },
              { text: "💰 Купить пакет хитов", action: "buy_hits" },
              { text: "🎵 Создать другой хит", action: "new_hit_after_demo" },
            ]
          );
          setSimState((s) => ({ ...s, step: "demo_ready" }));
        }, 2700);
        break;

      // ====== ENTER PROMO ======
      case "enter_promo":
        addMsg("user", "🎟 Ввести промокод");
        setTimeout(() => {
          addMsg("bot", `🎟 <b>Введите промокод:</b>\n\nОтправьте ваш промокод сообщением 👇`, [
            { text: "(Ввести DEMO2025)", action: "promo_applied" },
          ]);
        }, 400);
        break;

      case "promo_applied":
        addMsg("user", "DEMO2025");
        setTimeout(() => {
          addMsg(
            "bot",
            `🎉 <b>Промокод активирован!</b>\n\n✨ +1 хитов получено\n🎶 Автоматически открываем полную версию твоей песни…`
          );
          setTimeout(() => {
            addMsg("bot", `🔥 <b>Полная версия 1</b> твоего хита!`, undefined, { isAudio: true });
            addMsg("bot", `🔥 <b>Полная версия 2</b> твоего хита!`, undefined, { isAudio: true });
            setTimeout(() => {
              addMsg(
                "bot",
                `✅ <b>Готово!</b>\n\n🎵 Обе полные версии отправлены выше!\n💎 Остаток: <b>0 хитов</b>`,
                [
                  { text: "✨ Сделать вау-подарком", action: "gift_start" },
                  { text: "🎵 Создать ещё хит", action: "new_hit_end" },
                ]
              );
              setSimState((s) => ({ ...s, step: "full_delivered" }));
            }, 300);
          }, 600);
        }, 400);
        break;

      // ====== BUY FULL TRACK (single payment) ======
      case "buy_full_direct":
        addMsg("user", `💳 Оплатить ${PRICE}₽`);
        setTimeout(() => {
          addMsg(
            "bot",
            `🎵 <b>Получить полную версию</b>\n\n💡 Купи пакет WOW-хитов со скидкой: /buy`,
            [
              { text: `💳 Оплатить ${PRICE}₽`, action: "payment_full_redirect" },
              { text: "❌ Отмена", action: "cancel_payment" },
            ]
          );
        }, 400);
        break;

      case "payment_full_redirect":
        addMsg("user", `💳 Оплатить ${PRICE}₽`);
        setTimeout(() => {
          // Simulate successful payment → full versions delivered
          addMsg("bot", `🔥 <b>Полная версия 1</b> твоего хита!`, undefined, { isAudio: true });
          addMsg("bot", `🔥 <b>Полная версия 2</b> твоего хита!`, undefined, { isAudio: true });
          setTimeout(() => {
            addMsg("bot", `🎉 <b>Ваш хит готов!</b>\n\nОбе полные версии отправлены выше 👆\nВы можете сохранить их или отправить напрямую.\n\nОстаток на балансе: <b>0</b> хитов`);
            // Gift upsell after 3 sec
            setTimeout(() => {
              addMsg(
                "bot",
                `🎁 <b>Преврати трек в настоящий вау-подарок</b>\n\nЭто не просто mp3.\n\nЭто:\n✨ эффект открытия\n🎨 премиальный дизайн\n💌 личное сообщение от тебя\n🔗 красивая страница для отправки\n\nСделай подарок, который действительно запомнят.`,
                [
                  { text: "✨ Сделать вау-подарком", action: "gift_start" },
                  { text: "Оставить как есть", action: "end_silent" },
                ],
                { isPhoto: true }
              );
              setSimState((s) => ({ ...s, step: "full_delivered" }));
            }, 1000);
          }, 300);
        }, 800);
        break;

      // ====== BUY HITS PACKAGES ======
      case "buy_hits":
        addMsg("user", "💰 Купить пакет хитов");
        setTimeout(() => {
          const pkgs = packages || {};
          const btns = Object.entries(pkgs)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([qty, pkg]) => ({
              text: `${pkg.emoji} ${pkg.name} — ${pkg.price} ₽${pkg.savings ? ` (${pkg.savings})` : ""}`,
              action: `buy:${qty}`,
            }));
          addMsg(
            "bot",
            `🎵 <b>Что входит в 1 Хит:</b>\n\n• новый текст песни\n• 2 демо-версии\n• полная финальная версия песни\n\nВыберите пакет:`,
            btns
          );
          setSimState((s) => ({ ...s, step: "buy_packages" }));
        }, 400);
        break;

      case "package_selected": {
        const qty = (payload as string).replace("buy:", "");
        const pkg = packages?.[qty];
        addMsg("user", pkg ? `${pkg.emoji} ${pkg.name} — ${pkg.price} ₽` : `Пакет ${qty}`);
        setTimeout(() => {
          addMsg(
            "bot",
            `💳 <b>Оплата ${pkg?.name || qty}</b>\n\n💰 Сумма: ${pkg?.price || "???"}₽\n\n👇 Нажми кнопку для перехода к оплате:`,
            [
              { text: `💳 Перейти к оплате`, action: "payment_redirect" },
              { text: "❌ Отмена", action: "cancel_payment" },
            ]
          );
          setSimState((s) => ({ ...s, step: "payment", selectedQty: qty }));
        }, 400);
        break;
      }

      case "payment_redirect": {
        addMsg("user", "💳 Перейти к оплате");
        const paidQty = simState.selectedQty || "5";
        setTimeout(() => {
          // Simulate payment success → full versions auto-sent + use_hit
          addMsg("bot", `🔥 <b>Полная версия 1</b> твоего хита!`, undefined, { isAudio: true });
          addMsg("bot", `🔥 <b>Полная версия 2</b> твоего хита!`, undefined, { isAudio: true });
          setTimeout(() => {
            const remaining = Math.max(Number(paidQty) - 1, 0);
            addMsg(
              "bot",
              `✅ <b>Хит использован!</b>\n\n💎 Остаток на балансе: ${remaining} хитов\n\n🎵 Обе полные версии отправлены выше!\n\n━━━━━━━━━━━━━━━\n\n🎁 <b>Преврати трек в настоящий вау-подарок</b>\n— эффект открытия ✨\n— премиальный дизайн\n— личное сообщение`,
              [
                { text: "✨ Сделать вау-подарком", action: "gift_start" },
                { text: "🎵 Создать ещё хит", action: "new_hit_end" },
              ]
            );
            setSimState((s) => ({ ...s, step: "full_delivered" }));
          }, 300);
        }, 800);
        break;
      }

      // ====== GIFT MODE FLOW ======
      case "gift_start":
        addMsg("user", "✨ Сделать вау-подарком");
        setTimeout(() => {
          addMsg(
            "bot",
            `✨ <b>Вот как это будет выглядеть</b> 👇\n\nЧеловек открывает ссылку…\nНажимает «Открыть»…\nИ видит персональный музыкальный подарок.\n\nЭто уже не просто файл.\nЭто эмоция. 💫\n\n🔗 Пример: hitchef.ru/g/test-slug`,
            [
              { text: "🎁 Упаковать за 49 ₽", action: "gift_version_select" },
              { text: "◀️ Назад", action: "cancel_payment" },
            ]
          );
        }, 400);
        break;

      case "gift_version_select":
        addMsg("user", "🎁 Упаковать за 49 ₽");
        setTimeout(() => {
          addMsg(
            "bot",
            `🎵 <b>Какую версию использовать в подарке?</b>\n\nОбе версии были отправлены выше — послушай и выбери лучшую:`,
            [
              { text: "🎵 Версия 1", action: "gift_name" },
              { text: "🎵 Версия 2", action: "gift_name" },
            ]
          );
        }, 400);
        break;

      case "gift_name":
        addMsg("user", "🎵 Версия 1");
        setTimeout(() => {
          addMsg(
            "bot",
            `✅ Версия 1 выбрана!\n\n✍️ <b>Введи имя получателя:</b>\n\n<i>Например: Иван, Мама, Катя</i>`,
            [{ text: '(Ввести "Антон")', action: "gift_sender" }]
          );
        }, 400);
        break;

      case "gift_sender":
        addMsg("user", "Антон");
        setTimeout(() => {
          addMsg(
            "bot",
            `✅ Получатель: <b>Антон</b>\n\n👤 <b>От кого подарок?</b>\n\n<i>Например: Саша, Мама и Папа, Коллеги</i>\n\nИли нажми "Пропустить"`,
            [
              { text: '(Ввести "Саша")', action: "gift_style" },
              { text: "⏭ Пропустить", action: "gift_style_no_sender" },
            ]
          );
        }, 400);
        break;

      case "gift_style_no_sender":
        addMsg("user", "⏭ Пропустить");
        setTimeout(() => {
          addMsg(
            "bot",
            `✅ Получатель: <b>Антон</b>\n\n🎨 <b>Выбери стиль подарочной страницы:</b>`,
            [
              { text: "☀️ Light Premium", action: "gift_message" },
              { text: "🖤 Black Premium", action: "gift_message_black" },
            ]
          );
        }, 400);
        break;

      case "gift_style":
        addMsg("user", "Саша");
        setTimeout(() => {
          addMsg(
            "bot",
            `✅ Получатель: <b>Антон</b>\n✅ От: <b>Саша</b>\n\n🎨 <b>Выбери стиль подарочной страницы:</b>`,
            [
              { text: "☀️ Light Premium", action: "gift_message" },
              { text: "🖤 Black Premium", action: "gift_message_black" },
            ]
          );
        }, 400);
        break;

      case "gift_message_black":
        addMsg("user", "🖤 Black Premium");
        setTimeout(() => {
          addMsg(
            "bot",
            `✅ Стиль: <b>🖤 Black Premium</b>\n\n💌 <b>Хочешь добавить сообщение для получателя?</b>\n\n💡 Подсказка: <i>«С днём рождения! Этот трек только для тебя ❤️»</i>\n\n✍️ Напиши текст (до 300 символов) или нажми "Пропустить"`,
            [
              { text: "✍️ Ввести сообщение", action: "gift_enter_msg" },
              { text: "⏭ Пропустить", action: "gift_payment_skip_msg" },
            ]
          );
        }, 400);
        break;

      case "gift_message":
        addMsg("user", "☀️ Light Premium");
        setTimeout(() => {
          addMsg(
            "bot",
            `✅ Стиль: <b>☀️ Light Premium</b>\n\n💌 <b>Хочешь добавить сообщение для получателя?</b>\n\n💡 Подсказка: <i>«С днём рождения! Этот трек только для тебя ❤️»</i>\n\n✍️ Напиши текст (до 300 символов) или нажми "Пропустить"`,
            [
              { text: "✍️ Ввести сообщение", action: "gift_enter_msg" },
              { text: "⏭ Пропустить", action: "gift_payment_skip_msg" },
            ]
          );
        }, 400);
        break;

      case "gift_enter_msg":
        addMsg("user", "✍️ Ввести сообщение");
        setTimeout(() => {
          addMsg("bot", `💌 <b>Напиши сообщение для получателя</b> (до 300 символов):\n\n<i>Подсказка: «С днём рождения! Этот трек только для тебя ❤️»</i>`, [
            { text: '(Написать поздравление)', action: "gift_payment" },
          ]);
        }, 400);
        break;

      case "gift_payment_skip_msg":
        addMsg("user", "⏭ Пропустить");
        setTimeout(() => {
          addMsg(
            "bot",
            `🎁 <b>Упаковка подарка</b>\n\n👤 Получатель: <b>Антон</b>\n🎨 Стиль: <b>☀️ Light Premium</b>\n\n💰 Стоимость: <b>49₽</b>\n\n👇 Оплатите для создания gift-страницы:`,
            [
              { text: "💳 Оплатить 49₽", action: "gift_paid" },
              { text: "❌ Отмена", action: "cancel_payment" },
            ]
          );
        }, 400);
        break;

      case "gift_payment":
        addMsg("user", "С днём рождения, Антоха! 🎂🎣");
        setTimeout(() => {
          addMsg("bot", `✨ <b>Всё готово!</b>\nСоздаём твой музыкальный подарок…`);
          setTimeout(() => {
            addMsg(
              "bot",
              `🎁 <b>Упаковка подарка</b>\n\n👤 Получатель: <b>Антон</b>\n🎨 Стиль: <b>☀️ Light Premium</b>\n💌 Сообщение: <i>С днём рождения, Антоха! 🎂🎣</i>\n\n💰 Стоимость: <b>49₽</b>\n\n👇 Оплатите для создания gift-страницы:`,
              [
                { text: "💳 Оплатить 49₽", action: "gift_paid" },
                { text: "❌ Отмена", action: "cancel_payment" },
              ]
            );
          }, 600);
        }, 400);
        break;

      case "gift_paid":
        addMsg("user", "💳 Оплатить 49₽");
        setTimeout(() => {
          addMsg(
            "bot",
            `🎁 <b>Gift-страница создана!</b>\n\n🔗 Ссылка:\n<code>https://hitchef.ru/g/антон-abc123</code>\n\nОтправь ссылку получателю — он откроет и услышит персональный хит! ✨`,
            [
              { text: "📋 Скопировать ссылку", action: "gift_copy" },
              { text: "📤 Поделиться в Telegram", action: "end_silent" },
              { text: "📤 Поделиться в VK", action: "end_silent" },
            ]
          );
          setSimState((s) => ({ ...s, step: "gift_done" }));
        }, 800);
        break;

      case "gift_copy":
        addMsg("user", "📋 Скопировать ссылку");
        setTimeout(() => {
          addMsg("bot", `🔗 <b>Ссылка скопирована:</b>\n\n<code>https://hitchef.ru/g/антон-abc123</code>\n\n💡 <i>Нажми на ссылку выше, чтобы скопировать</i>`);
        }, 300);
        break;

      // ====== CANCEL & MISC ======
      case "cancel_payment":
        addMsg("user", "❌ Отмена");
        setTimeout(() => {
          addMsg("bot", `❌ Оплата отменена. Напиши /buy чтобы выбрать другой пакет.`);
        }, 300);
        break;

      case "new_hit_after_demo":
      case "new_hit_end":
        addMsg("user", "🎵 Создать ещё хит");
        setTimeout(() => {
          const btns = (events || []).map((e) => ({
            text: `${e.emoji} ${e.name}`,
            action: `event:${e.id}:${e.emoji} ${e.name}`,
          }));
          addMsg("bot", `🎵 <b>Создаём новый хит!</b>\n\nВыбери событие для песни:`, btns);
          setSimState({ step: "select_event" });
        }, 400);
        break;

      case "end_silent":
        // No-op
        break;

      default:
        break;
    }
  };

  const handleButtonClick = (action: string) => {
    if (action.startsWith("event:")) {
      runStep("event_selected", action);
    } else if (action.startsWith("genre:")) {
      runStep("genre_selected", action);
    } else if (action.startsWith("vocal:")) {
      runStep("vocal_selected", action);
    } else if (action.startsWith("buy:")) {
      runStep("package_selected", action);
    } else {
      runStep(action);
    }
  };

  return (
    <AdminLayout title="Симулятор бота">
      <div className="flex flex-col items-center gap-4">
        {/* Controls */}
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={reset} className="gap-2">
            <RotateCcw className="w-3.5 h-3.5" />
            Начать заново
          </Button>
          <span className="text-xs text-muted-foreground">
            Интерактивный предосмотр воронки Telegram-бота
          </span>
        </div>

        {/* Phone frame */}
        <div className="w-[375px] h-[700px] bg-background border-2 border-border rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col relative">
          {/* Status bar */}
          <div className="h-11 bg-card flex items-center justify-between px-6 text-[11px] text-muted-foreground border-b border-border flex-shrink-0">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <Smartphone className="w-3 h-3" />
            </div>
          </div>

          {/* Chat header */}
          <div className="h-12 bg-card border-b border-border flex items-center px-4 gap-3 flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm">👨‍🍳</div>
            <div>
              <p className="text-sm font-semibold">Хит Шеф</p>
              <p className="text-[10px] text-muted-foreground">бот</p>
            </div>
          </div>

          {/* Messages */}
          <div ref={chatRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-1 bg-muted/30">
            {messages.map((msg) => (
              <MessageBubble key={msg.id} msg={msg} onButtonClick={handleButtonClick} />
            ))}
          </div>

          {/* Input bar */}
          <div className="h-11 bg-card border-t border-border flex items-center px-4 gap-2 flex-shrink-0">
            <div className="flex-1 h-7 rounded-full bg-muted/50 border border-border px-3 flex items-center">
              <span className="text-[11px] text-muted-foreground">Сообщение...</span>
            </div>
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
              <ChevronRight className="w-3.5 h-3.5 text-primary" />
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
